import { createContext, useContext, useEffect } from 'react';

/**
 * Lets nested edit dialogs temporarily suspend QuizAgentDialog's quizHash
 * recomputation. RHF form values still flow through normally — only the
 * base64 hash that feeds every QuizFrame iframe is frozen, which is what
 * was causing every keystroke to re-postMessage every preview.
 *
 * On `resume()` an immediate recompute is performed so the preview catches
 * up with whatever was edited while the dialog was open.
 */
export interface IPreviewSyncContext {
  pause: () => void;
  resume: () => void;
}

export const PreviewSyncContext = createContext<IPreviewSyncContext | null>(null);

/**
 * Mount-and-forget hook: pauses preview-hash recomputation while the calling
 * component is mounted, resumes (and force-recomputes once) on unmount.
 * Safe to call from a component that may render outside the provider — it
 * silently no-ops in that case.
 */
export const usePausePreviewSync = () => {
  const ctx = useContext(PreviewSyncContext);
  useEffect(() => {
    if (!ctx) return;
    ctx.pause();
    return () => ctx.resume();
  }, [ctx]);
};
